import {
    and,
    AnyStateConfig,
    assertEvent,
    DoneActorEvent,
    enqueueActions,
    ErrorActorEvent,
    not,
    or,
    setup,
} from 'xstate';

import { FlowEntity, InitMachineParams } from '../../types';
import { getUserGuards, isStateGuard } from '../helpers';
import type { rcFlowContextOutputs } from '../redeemCoupon';

export type directFlowContext = {
    couponCode: string;
};

export const directFlow: FlowEntity = {
    initActor: (rawParams) => {
        const { config, actors, router, getConfigByKey, queryClient, region } =
            rawParams as InitMachineParams;
        const isState = isStateGuard({ getConfigByKey, flowId: 'direct', router, region });
        const userGuards = getUserGuards(queryClient);
        const { isSubscriber, isExSubscriber, isRegistered } = getUserGuards(queryClient);

        return setup({
            types: {
                events: {} as
                    | DoneActorEvent<rcFlowContextOutputs, 'redeemCoupon'>
                    | ErrorActorEvent<rcFlowContextOutputs, 'redeemCoupon'>
                    | { type: 'REDEEM_COUPON' },
            },
            guards: {
                isRedeemCoupon: and(isState('redeemCoupon', 'inclusion')),
                isNotAuthorized: not(or([isSubscriber, isExSubscriber, isRegistered])),
                ...userGuards,
            },
            actions: {
                redirectToSignIn: enqueueActions(({ enqueue, check, context }) => {
                    const route = <string>getConfigByKey('direct.destinationUrls.signIn');
                    const { couponCode } = context;

                    const originalUrl = `/account/direct/cr?promo=${couponCode}`;
                    const encodedRedirect = encodeURIComponent(originalUrl);

                    const signInRedirectionForRedeemCouponFlow = `${route}?redirectUrl=${encodedRedirect}`;

                    window.location.href = signInRedirectionForRedeemCouponFlow;
                }),
                returnToAccount: enqueueActions(({ enqueue, check, event }) => {
                    assertEvent(event, 'xstate.error.actor.redeemCoupon');
                    const errorUrl = getConfigByKey('direct.destinationUrls.error') as string;
                    window.location.href = errorUrl;
                }),
                persistQueryParams: enqueueActions(({ enqueue, check, event }) => {
                    const { promo } = router.query;
                    enqueue.assign({ couponCode: promo });
                }),
                returnToAccountSuccessfully: ({ event, context }) => {
                    assertEvent(event, 'xstate.done.actor.redeemCoupon');

                    context.formBuilderRef?.toggleLoading(false);
                    context.formBuilderRef?.toggleBanner({
                        message: 'Coupon applied successfully',
                        variant: 'success',
                    });
                    window.location.href = '/account';
                },
            },
            actors,
        }).createMachine({ ...(config as AnyStateConfig) });
    },
};
